// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The panel canvas.
 *
 * Built from deltaKi's `BoardView`, with everything about diffing and merging
 * removed and panel editing put in its place. The important inheritance is that
 * boards are drawn by the **real PCB renderer** rather than as flat outlines:
 * one display list per source file, drawn once per placement.
 *
 * Everything is drawn in board millimetres with the camera transform doing the
 * scaling, so a 0.25 mm track is asked for as 0.25 at every zoom.
 */

import { type BBox, bbox } from '../model/bbox.ts';
import type { Point } from '../model/geometry.ts';
import {
  BoardRenderer,
  Camera,
  DEFAULT_RENDER_OPTIONS,
  type Viewport,
} from '../render/index.ts';
import { pointInPolygon, type Polygon } from '../panel/outline.ts';
import { allCuts, type PanelPreview } from '../panel/preview.ts';
import type { PanelSpec, Vec2 } from '../panel/spec.ts';
import { basename, type Selection, type SourceFile } from './state.ts';

export type Tool = 'select' | 'tab' | 'rect' | 'text';

export interface PanelViewInput {
  preview: PanelPreview;
  spec: PanelSpec;
  sources: Map<string, SourceFile>;
  selection: Selection | null;
  visibleLayers: Set<string>;
  /** Stacking order, bottom first. The last layer named draws on top. */
  layerOrder?: readonly string[];
  /** Full-canvas crosshairs following the pointer, as KiCad's editor offers. */
  crosshair: boolean;
  tool: Tool;
  snap: number;
}

export interface ViewStats {
  shapesDrawn: number;
  ms: number;
}

/** Alpha for filled copper, so the layer beneath shows faintly through. */
const LAYER_FILL_OPACITY = 0.8;

/** How close, in screen pixels, counts as hitting a handle or a tab origin. */
const HANDLE_PX = 7;

type DragMode =
  | 'none' | 'pan' | 'board' | 'railmove' | 'tabmove' | 'textmove'
  | 'railcorner' | 'polyvertex' | 'tabnew' | 'rectnew';

interface DragState {
  mode: DragMode;
  startScreen: Point;
  startWorld: Point;
  world: Point;
  /** Index into whichever list the drag is editing. */
  index: number;
  corner: number;
  /** The value the drag started from, so each move recomputes from the original. */
  origin: Vec2;
  origin2: Vec2;
  /** For a corner resize: the rail's original x0, y0, x1, y1. */
  rect0: [number, number, number, number];
  moved: boolean;
  /** Whether this drag has already been announced as an edit. */
  begun: boolean;
  shift: boolean;
}

/** Drag modes that change the spec, as opposed to moving the view. */
const EDITING_MODES = new Set<DragMode>([
  'board', 'railmove', 'tabmove', 'textmove', 'railcorner', 'polyvertex',
]);

export class PanelView {
  readonly canvas: HTMLCanvasElement;
  readonly camera = new Camera();

  private readonly ctx: CanvasRenderingContext2D;
  private readonly overlay: HTMLCanvasElement | null;
  private readonly overlayCtx: CanvasRenderingContext2D | null;
  private overlayPending = false;
  private readonly renderer: BoardRenderer;
  private input: PanelViewInput | null = null;
  private drag: DragState | null = null;
  /** Fingers on the canvas, by pointer id, in client coordinates. */
  private touches = new Map<number, { x: number; y: number }>();
  /** Finger spread and midpoint (canvas pixels) at the last pinch step. */
  private pinch: { dist: number; x: number; y: number } | null = null;
  /**
   * A pinch ended with a finger still down. That finger does nothing until it
   * lifts; otherwise it would pan, or grab whatever is under it.
   */
  private gestureSpent = false;
  private pending = false;
  private fitted = false;
  /** Last pointer position in board coordinates, for the crosshairs. */
  private pointer: Point | null = null;

  onSelect?: (selection: Selection | null) => void;
  /** A drag has started changing the spec: the moment to record an undo point. */
  onEditBegin?: () => void;
  onCommit?: (change: (spec: PanelSpec) => void) => void;
  onPreviewChange?: (change: (spec: PanelSpec) => void) => void;
  onNewTab?: (origin: Vec2, direction: Vec2) => void;
  onNewRect?: (at: Vec2, size: Vec2) => void;
  onNewText?: (at: Vec2) => void;
  onStats?: (stats: ViewStats) => void;
  /** Where the pointer is, in board millimetres, or null when it has left. */
  onPointerAt?: (at: Point | null) => void;

  /**
   * @param overlay A second canvas stacked over the first, for anything that
   *   follows the pointer. Redrawing the board on every mouse move costs a full
   *   repaint — grid, every placed board through the renderer, rails, tabs —
   *   which is why the crosshair trailed the cursor. Two lines on an empty
   *   overlay cost nothing.
   */
  constructor(canvas: HTMLCanvasElement, overlay?: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (ctx === null) throw new Error('this browser cannot provide a 2D canvas');
    this.canvas = canvas;
    this.ctx = ctx;
    this.overlay = overlay ?? null;
    this.overlayCtx = overlay?.getContext('2d') ?? null;
    this.renderer = new BoardRenderer(ctx, globalThis.devicePixelRatio ?? 1);

    canvas.addEventListener('wheel', this.onWheel, { passive: false });
    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.onPointerUp);
    canvas.addEventListener('contextmenu', (ev) => ev.preventDefault());
    canvas.addEventListener('pointerleave', () => {
      if (this.pointer === null) return;
      this.pointer = null;
      this.onPointerAt?.(null);
      this.drawOverlay();
    });

    new ResizeObserver(() => {
      if (!this.fitted && this.fit()) this.fitted = true;
      this.requestDraw();
    }).observe(canvas);
  }

  setInput(input: PanelViewInput, refit = false): void {
    this.input = input;
    this.canvas.classList.toggle('tool', input.tool !== 'select');
    if (refit || !this.fitted) {
      if (this.fit()) this.fitted = true;
    }
    this.requestDraw();
  }

  private get viewport(): Viewport {
    return { width: this.canvas.clientWidth, height: this.canvas.clientHeight };
  }

  fit(box?: BBox | null): boolean {
    const target = box ?? this.input?.preview.panelBBox ?? null;
    if (target === null) return false;
    const margin = Math.max(
      2,
      Math.min((target.maxX - target.minX) * 0.08, (target.maxY - target.minY) * 0.08),
    );
    return this.camera.fit(
      bbox(target.minX - margin, target.minY - margin, target.maxX + margin, target.maxY + margin),
      this.viewport,
      24,
    );
  }

  requestDraw(): void {
    if (this.pending) return;
    this.pending = true;
    requestAnimationFrame(() => {
      this.pending = false;
      this.draw();
    });
  }

  /**
   * The clickable box around a text item, in board coordinates.
   *
   * Sized from the glyphs but never smaller than a fingertip on screen: a
   * target measured only in millimetres shrinks with the zoom, and on a large
   * panel a 1.5 mm tall label becomes a two-pixel sliver that cannot be hit.
   */
  private textBox(item: { text?: string; at?: Vec2; height?: number }): {
    cx: number; cy: number; halfW: number; halfH: number;
  } | null {
    const at = item.at;
    if (!Array.isArray(at)) return null;
    const size = item.height ?? 1.5;
    const floor = this.camera.pixelSize(HANDLE_PX);
    return {
      cx: at[0],
      cy: at[1],
      halfW: Math.max((item.text ?? '').length * size * 0.35, floor),
      halfH: Math.max(size * 0.7, floor),
    };
  }

  /** World coordinates of a pointer event. */
  private worldOf(ev: { clientX: number; clientY: number }): Point {
    const rect = this.canvas.getBoundingClientRect();
    return [
      this.camera.toWorldX(ev.clientX - rect.left),
      this.camera.toWorldY(ev.clientY - rect.top),
    ];
  }

  private snap(v: number): number {
    const step = this.input?.snap ?? 0;
    if (!(step > 0)) return Math.round(v * 1e3) / 1e3;
    return Math.round(v / step) * step;
  }

  private snapPoint(p: Point): Vec2 {
    return [this.snap(p[0]), this.snap(p[1])];
  }

  // --- drawing ----------------------------------------------------------------------

  private draw(): void {
    const input = this.input;
    const view = this.viewport;
    if (view.width === 0 || view.height === 0) return;

    const dpr = globalThis.devicePixelRatio ?? 1;
    const ctx = this.ctx;
    this.canvas.width = Math.round(view.width * dpr);
    this.canvas.height = Math.round(view.height * dpr);

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#14141a';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    if (input === null) return;
    const started = performance.now();
    let shapesDrawn = 0;

    this.drawGrid(view, dpr);

    // Boards, through the real renderer, one display list per source file.
    for (const board of input.preview.boards) {
      if (board.placement === null || board.error !== null) continue;
      const source = input.sources.get(basename(board.file));
      if (source === undefined) continue;
      shapesDrawn += this.renderer.draw(source.displayList, this.camera, view, {
        ...DEFAULT_RENDER_OPTIONS,
        visibleLayers: input.visibleLayers,
        // A touch of transparency so a layer underneath still reads through the
        // one above it, which is most of the point of restacking them.
        fillOpacity: LAYER_FILL_OPACITY,
        ...(input.layerOrder ? { layerOrder: input.layerOrder } : {}),
        drawText: this.camera.scale > 6,
        placement: board.placement,
        // One file can hold many boards; draw only the one that was placed.
        ...(board.selectionBounds !== null ? { clipTo: board.selectionBounds } : {}),
      }).shapesDrawn;
    }

    ctx.save();
    this.camera.applyTo(ctx, dpr);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    const px = (n: number): number => this.camera.pixelSize(n);

    // Rails and patches sit under the boards conceptually but are drawn after,
    // because their translucent fill reads better over the copper.
    for (const rail of input.preview.rails) {
      this.fillPolys(rail.polys, '#6a6f7a55', '#9aa0ad', px(1.2));
    }
    for (const patch of input.preview.patches) {
      this.fillPolys(patch.polys, '#6a6f7a33', '#9aa0ad88', px(1));
    }

    // A translucent wash over each board so it reads as a solid piece even with
    // only Edge.Cuts visible.
    for (const board of input.preview.boards) {
      this.fillPolys(board.polys, '#2b7d3f33', '#58c878', px(1.4));
    }

    for (const tab of input.preview.tabs) {
      this.fillPolys(tab.polys, '#e3a34155', '#e3a341', px(1.2));
    }

    // Cuts last so they stay legible over everything they cross.
    ctx.strokeStyle = '#f85149';
    ctx.lineWidth = px(1.6);
    ctx.setLineDash([px(4), px(3)]);
    for (const cut of allCuts(input.preview)) {
      if (cut.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(cut[0]![0], cut[0]![1]);
      for (let i = 1; i < cut.length; i++) ctx.lineTo(cut[i]![0], cut[i]![1]);
      ctx.stroke();
    }
    ctx.setLineDash([]);

    this.drawTabOrigins(input, px);
    this.drawTexts(input);
    this.drawClearanceWarnings(input, px);
    this.drawDimensions(input, px);
    this.drawSelection(input, px);
    this.drawGhost(px);

    ctx.restore();

    this.drawOverlay();
    this.onStats?.({ shapesDrawn, ms: performance.now() - started });
  }

  private fillPolys(polys: Polygon[], fill: string, stroke: string, width: number): void {
    const ctx = this.ctx;
    for (const poly of polys) {
      const path = new Path2D();
      ringPath(path, poly.ext);
      for (const hole of poly.holes) ringPath(path, hole);
      ctx.fillStyle = fill;
      ctx.fill(path, 'evenodd');
      ctx.strokeStyle = stroke;
      ctx.lineWidth = width;
      ctx.stroke(path);
    }
  }

  private drawGrid(view: Viewport, dpr: number): void {
    const ctx = this.ctx;
    const scale = this.camera.scale;
    // Adaptive step, carried over from the original tool's canvas.
    const step = scale > 25 ? 1 : scale > 5 ? 5 : scale > 1.2 ? 10 : 50;
    const box = this.camera.visibleBox(view);

    ctx.save();
    this.camera.applyTo(ctx, dpr);
    ctx.lineWidth = this.camera.pixelSize(1);

    const x0 = Math.floor(box.minX / step) * step;
    const y0 = Math.floor(box.minY / step) * step;
    for (let x = x0; x <= box.maxX; x += step) {
      ctx.strokeStyle = Math.round(x / step) % 5 === 0 ? '#2c2c36' : '#212129';
      ctx.beginPath();
      ctx.moveTo(x, box.minY);
      ctx.lineTo(x, box.maxY);
      ctx.stroke();
    }
    for (let y = y0; y <= box.maxY; y += step) {
      ctx.strokeStyle = Math.round(y / step) % 5 === 0 ? '#2c2c36' : '#212129';
      ctx.beginPath();
      ctx.moveTo(box.minX, y);
      ctx.lineTo(box.maxX, y);
      ctx.stroke();
    }
    ctx.restore();
  }

  private drawTabOrigins(input: PanelViewInput, px: (n: number) => number): void {
    const ctx = this.ctx;
    (input.spec.tabs ?? []).forEach((tab, index) => {
      if (!Array.isArray(tab?.origin)) return;
      const [x, y] = tab.origin;
      const failed = input.preview.tabs[index]?.error != null;
      ctx.beginPath();
      ctx.arc(x, y, px(3.5), 0, Math.PI * 2);
      ctx.fillStyle = failed ? '#f85149' : '#e3a341';
      ctx.fill();

      if (failed) {
        // A ring around the origin, so a tab that produced no geometry is still
        // visible and selectable rather than simply missing.
        ctx.beginPath();
        ctx.arc(x, y, px(9), 0, Math.PI * 2);
        ctx.strokeStyle = '#f85149';
        ctx.lineWidth = px(1.5);
        ctx.stroke();
      }
    });
  }

  private drawTexts(input: PanelViewInput): void {
    const ctx = this.ctx;
    for (const item of input.spec.text ?? []) {
      if (!Array.isArray(item?.at)) continue;
      const size = item.height ?? 1.5;
      ctx.save();
      ctx.translate(item.at[0], item.at[1]);
      if (item.rotation) ctx.rotate((-item.rotation * Math.PI) / 180);
      ctx.fillStyle = '#d8d8e0';
      ctx.font = `${size}px ${'ui-monospace, monospace'}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(item.text ?? '', 0, 0);
      ctx.restore();
    }
  }

  /**
   * Overall panel size, drawn as KiCad-style dimensions.
   *
   * Only once a frame or edge rails exist: until then the outline is whatever
   * the boards happen to span, which is not yet a manufacturable size. This is
   * the number you give a fabricator, so it is worth having on screen rather
   * than in a status line.
   */
  private drawDimensions(input: PanelViewInput, px: (n: number) => number): void {
    const box = input.preview.panelBBox;
    if (box === null) return;
    const outer = (input.spec.rails ?? []).some(
      (r) => r?.type === 'frame' || r?.type === 'tb' || r?.type === 'lr',
    );
    if (!outer) return;

    const ctx = this.ctx;
    const gap = px(26);
    const overshoot = px(6);

    ctx.save();
    ctx.strokeStyle = '#8a97a3';
    ctx.fillStyle = '#c8d0d8';
    ctx.lineWidth = px(1);
    ctx.font = `${px(11)}px ui-monospace, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';

    // Horizontal, below the panel.
    const hy = box.maxY + gap;
    this.extensionLine(box.minX, box.maxY, box.minX, hy + overshoot);
    this.extensionLine(box.maxX, box.maxY, box.maxX, hy + overshoot);
    this.dimensionLine(box.minX, hy, box.maxX, hy, px);
    ctx.fillText(`${(box.maxX - box.minX).toFixed(2)} mm`, (box.minX + box.maxX) / 2, hy - px(3));

    // Vertical, to the left, with the text rotated to run along it.
    const vx = box.minX - gap;
    this.extensionLine(box.minX, box.minY, vx - overshoot, box.minY);
    this.extensionLine(box.minX, box.maxY, vx - overshoot, box.maxY);
    this.dimensionLine(vx, box.minY, vx, box.maxY, px);

    ctx.save();
    ctx.translate(vx - px(3), (box.minY + box.maxY) / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText(`${(box.maxY - box.minY).toFixed(2)} mm`, 0, 0);
    ctx.restore();

    ctx.restore();
  }

  /** Schedule an overlay repaint — cheap, so it can run on every pointer move. */
  private requestOverlayDraw(): void {
    if (this.overlayPending) return;
    this.overlayPending = true;
    requestAnimationFrame(() => {
      this.overlayPending = false;
      this.drawOverlay();
    });
  }

  /**
   * Everything that follows the pointer, on its own canvas.
   *
   * Kept apart from `draw()` precisely so it can be repainted on every mouse
   * move: clearing two lines is free, whereas redrawing the board is not, and
   * doing the latter left the crosshair a full frame behind the cursor.
   */
  private drawOverlay(): void {
    const ctx = this.overlayCtx;
    const overlay = this.overlay;
    if (ctx === null || overlay === null) return;

    const view = this.viewport;
    if (view.width === 0 || view.height === 0) return;
    const dpr = globalThis.devicePixelRatio ?? 1;

    const width = Math.round(view.width * dpr);
    const height = Math.round(view.height * dpr);
    // Assigning the size clears the canvas, so only do it when it changed.
    if (overlay.width !== width || overlay.height !== height) {
      overlay.width = width;
      overlay.height = height;
    } else {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, width, height);
    }

    if (this.input?.crosshair !== true || this.pointer === null) return;

    ctx.save();
    this.camera.applyTo(ctx, dpr);
    const [x, y] = this.pointer;
    const box = this.camera.visibleBox(view);

    ctx.strokeStyle = '#c8d0d8aa';
    ctx.lineWidth = this.camera.pixelSize(1);
    ctx.beginPath();
    ctx.moveTo(box.minX, y);
    ctx.lineTo(box.maxX, y);
    ctx.moveTo(x, box.minY);
    ctx.lineTo(x, box.maxY);
    ctx.stroke();
    ctx.restore();
  }

  private extensionLine(x0: number, y0: number, x1: number, y1: number): void {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();
  }

  /** A dimension line with an arrowhead at each end. */
  private dimensionLine(
    x0: number, y0: number, x1: number, y1: number,
    px: (n: number) => number,
  ): void {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();

    const angle = Math.atan2(y1 - y0, x1 - x0);
    this.arrowHead(x0, y0, angle, px);
    this.arrowHead(x1, y1, angle + Math.PI, px);
  }

  private arrowHead(x: number, y: number, angle: number, px: (n: number) => number): void {
    const ctx = this.ctx;
    const length = px(7);
    const spread = 0.28;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(angle - spread) * length, y + Math.sin(angle - spread) * length);
    ctx.lineTo(x + Math.cos(angle + spread) * length, y + Math.sin(angle + spread) * length);
    ctx.closePath();
    ctx.fill();
  }

  /**
   * Ring the rails that will fuse into a board.
   *
   * A 0 mm gap and a 0.05 mm gap look identical at panel zoom, so the warning
   * has to be drawn rather than merely written in the console.
   */
  private drawClearanceWarnings(input: PanelViewInput, px: (n: number) => number): void {
    if (input.preview.clearance.length === 0) return;
    const ctx = this.ctx;
    const flagged = new Map<number, 'merge' | 'tight'>();
    for (const issue of input.preview.clearance) {
      if (flagged.get(issue.railIndex) !== 'merge') flagged.set(issue.railIndex, issue.severity);
    }

    for (const [railIndex, severity] of flagged) {
      const rail = input.preview.rails[railIndex];
      if (rail === undefined) continue;
      ctx.save();
      ctx.strokeStyle = severity === 'merge' ? '#f85149' : '#e3b341';
      ctx.lineWidth = px(2);
      ctx.setLineDash([px(5), px(3)]);
      for (const poly of rail.polys) {
        const path = new Path2D();
        ringPath(path, poly.ext);
        for (const hole of poly.holes) ringPath(path, hole);
        ctx.stroke(path);
      }
      ctx.restore();
    }
  }

  private drawSelection(input: PanelViewInput, px: (n: number) => number): void {
    const selection = input.selection;
    if (selection === null) return;
    const ctx = this.ctx;
    ctx.strokeStyle = '#ff9d3c';
    ctx.lineWidth = px(2.5);

    if (selection.kind === 'board') {
      const board = input.preview.boards[selection.index];
      if (board) this.outline(board.polys);
    } else if (selection.kind === 'rail') {
      const rail = input.preview.rails[selection.index];
      if (rail) this.outline(rail.polys);
      this.drawRailHandles(input, selection.index, px);
    } else if (selection.kind === 'patch') {
      const patch = input.preview.patches[selection.index];
      if (patch) this.outline(patch.polys);
    } else if (selection.kind === 'tab') {
      const tab = input.preview.tabs[selection.index];
      if (tab) this.outline(tab.polys);
      const spec = (input.spec.tabs ?? [])[selection.index];
      if (spec && Array.isArray(spec.origin)) {
        ctx.beginPath();
        ctx.arc(spec.origin[0], spec.origin[1], px(6), 0, Math.PI * 2);
        ctx.stroke();
        // The direction the ray points, so a tab reads as aimed rather than placed.
        if (Array.isArray(spec.direction)) {
          const len = Math.hypot(spec.direction[0], spec.direction[1]) || 1;
          ctx.beginPath();
          ctx.moveTo(spec.origin[0], spec.origin[1]);
          ctx.lineTo(
            spec.origin[0] + (spec.direction[0] / len) * px(26),
            spec.origin[1] + (spec.direction[1] / len) * px(26),
          );
          ctx.stroke();
        }
      }
    } else if (selection.kind === 'text') {
      // The same box that accepts the click, so the highlight tells the truth.
      const box = this.textBox((input.spec.text ?? [])[selection.index] ?? {});
      if (box !== null) {
        ctx.strokeRect(box.cx - box.halfW, box.cy - box.halfH, box.halfW * 2, box.halfH * 2);
      }
    }
  }

  private outline(polys: Polygon[]): void {
    const ctx = this.ctx;
    for (const poly of polys) {
      const path = new Path2D();
      ringPath(path, poly.ext);
      for (const hole of poly.holes) ringPath(path, hole);
      ctx.stroke(path);
    }
  }

  private drawRailHandles(input: PanelViewInput, index: number, px: (n: number) => number): void {
    const rail = (input.spec.rails ?? [])[index];
    if (rail === undefined) return;
    const ctx = this.ctx;
    ctx.fillStyle = '#ff9d3c';

    if (rail.type === 'rect' && Array.isArray(rail.at) && Array.isArray(rail.size)) {
      const size = px(4);
      for (const [cx, cy] of rectCorners(rail.at, rail.size)) {
        ctx.fillRect(cx - size / 2, cy - size / 2, size, size);
      }
    } else if (rail.type === 'poly' && Array.isArray(rail.points)) {
      for (const [cx, cy] of rail.points) {
        ctx.beginPath();
        ctx.arc(cx, cy, px(3.5), 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  private drawGhost(px: (n: number) => number): void {
    const drag = this.drag;
    if (drag === null) return;
    const ctx = this.ctx;
    ctx.strokeStyle = '#4a9eff';
    ctx.lineWidth = px(1.5);
    ctx.setLineDash([px(4), px(3)]);

    if (drag.mode === 'tabnew') {
      const [dx, dy] = this.dragDirection(drag);
      ctx.beginPath();
      ctx.moveTo(drag.startWorld[0], drag.startWorld[1]);
      ctx.lineTo(drag.startWorld[0] + dx * px(40), drag.startWorld[1] + dy * px(40));
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#4a9eff';
      ctx.beginPath();
      ctx.arc(drag.startWorld[0], drag.startWorld[1], px(3.5), 0, Math.PI * 2);
      ctx.fill();
    } else if (drag.mode === 'rectnew') {
      const x = Math.min(drag.startWorld[0], drag.world[0]);
      const y = Math.min(drag.startWorld[1], drag.world[1]);
      ctx.strokeRect(x, y, Math.abs(drag.world[0] - drag.startWorld[0]), Math.abs(drag.world[1] - drag.startWorld[1]));
    }
    ctx.setLineDash([]);
  }

  // --- hit testing ------------------------------------------------------------------

  private hitTest(world: Point): Selection | null {
    const input = this.input;
    if (input === null) return null;
    const tolerance = this.camera.pixelSize(HANDLE_PX);
    const [x, y] = world;

    // Tab origins first: they are small, and often sit on top of other things.
    const tabs = input.spec.tabs ?? [];
    for (let i = tabs.length - 1; i >= 0; i--) {
      const origin = tabs[i]?.origin;
      if (Array.isArray(origin) && Math.hypot(origin[0] - x, origin[1] - y) <= tolerance) {
        return { kind: 'tab', index: i };
      }
    }

    const texts = input.spec.text ?? [];
    for (let i = texts.length - 1; i >= 0; i--) {
      const box = this.textBox(texts[i] ?? {});
      if (box === null) continue;
      if (Math.abs(box.cx - x) <= box.halfW && Math.abs(box.cy - y) <= box.halfH) {
        return { kind: 'text', index: i };
      }
    }

    for (let i = input.preview.tabs.length - 1; i >= 0; i--) {
      if (input.preview.tabs[i]!.polys.some((p) => pointInPolygon(p, x, y))) {
        return { kind: 'tab', index: i };
      }
    }
    for (let i = input.preview.boards.length - 1; i >= 0; i--) {
      if (input.preview.boards[i]!.polys.some((p) => pointInPolygon(p, x, y))) {
        return { kind: 'board', index: i };
      }
    }
    for (let i = input.preview.rails.length - 1; i >= 0; i--) {
      if (input.preview.rails[i]!.polys.some((p) => pointInPolygon(p, x, y))) {
        return { kind: 'rail', index: i };
      }
    }
    for (let i = input.preview.patches.length - 1; i >= 0; i--) {
      if (input.preview.patches[i]!.polys.some((p) => pointInPolygon(p, x, y))) {
        return { kind: 'patch', index: i };
      }
    }
    return null;
  }

  /** A handle of the current selection, if the pointer is on one. */
  private hitHandle(world: Point): { mode: DragMode; index: number; corner: number } | null {
    const input = this.input;
    const selection = input?.selection;
    if (input === undefined || input === null || selection == null || selection.kind !== 'rail') return null;

    const rail = (input.spec.rails ?? [])[selection.index];
    if (rail === undefined) return null;
    const tolerance = this.camera.pixelSize(HANDLE_PX);

    if (rail.type === 'rect' && Array.isArray(rail.at) && Array.isArray(rail.size)) {
      const corners = rectCorners(rail.at, rail.size);
      for (let c = 0; c < corners.length; c++) {
        if (Math.hypot(corners[c]![0] - world[0], corners[c]![1] - world[1]) <= tolerance) {
          return { mode: 'railcorner', index: selection.index, corner: c };
        }
      }
    } else if (rail.type === 'poly' && Array.isArray(rail.points)) {
      for (let v = 0; v < rail.points.length; v++) {
        const p = rail.points[v]!;
        if (Math.hypot(p[0] - world[0], p[1] - world[1]) <= tolerance) {
          return { mode: 'polyvertex', index: selection.index, corner: v };
        }
      }
    }
    return null;
  }

  // --- interaction ------------------------------------------------------------------

  /**
   * Wheel behaviour, matching KiCad's PCB editor: plain wheel zooms, Shift
   * scrolls up and down, Ctrl scrolls left and right.
   *
   * Browsers rewrite `deltaY` to `deltaX` for shifted wheels on some platforms,
   * so whichever axis carries the movement is the one to use.
   */
  private onWheel = (ev: WheelEvent): void => {
    ev.preventDefault();
    const delta = ev.deltaY !== 0 ? ev.deltaY : ev.deltaX;

    if (ev.shiftKey) {
      this.camera.panByScreen(0, -delta);
    } else if (ev.ctrlKey || ev.metaKey) {
      this.camera.panByScreen(-delta, 0);
    } else {
      const rect = this.canvas.getBoundingClientRect();
      this.camera.zoomAt(ev.clientX - rect.left, ev.clientY - rect.top, delta < 0 ? 1.2 : 1 / 1.2);
    }
    this.requestDraw();
  };

  private onPointerDown = (ev: PointerEvent): void => {
    if (ev.pointerType === 'touch') {
      this.touches.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (this.touches.size === 2) {
        this.startPinch(ev);
        return;
      }
      if (this.touches.size > 2 || this.gestureSpent) return;
    }
    const input = this.input;
    if (input === null) return;
    this.canvas.setPointerCapture(ev.pointerId);
    const world = this.worldOf(ev);
    const base: DragState = {
      mode: 'none',
      startScreen: [ev.clientX, ev.clientY],
      startWorld: world,
      world,
      index: -1,
      corner: -1,
      origin: [0, 0],
      origin2: [0, 0],
      rect0: [0, 0, 0, 0],
      moved: false,
      begun: false,
      shift: ev.shiftKey,
    };

    // Middle button always pans, whatever the tool.
    if (ev.button === 1) {
      this.drag = { ...base, mode: 'pan' };
      this.canvas.classList.add('dragging');
      return;
    }

    // Shift-drag pans without grabbing whatever happens to be under the cursor.
    // The tab tool is the one exception: Shift already means "keep the true
    // angle" there, and losing that would cost more than it gains.
    if (ev.shiftKey && input.tool !== 'tab') {
      this.drag = { ...base, mode: 'pan' };
      this.canvas.classList.add('dragging');
      return;
    }

    if (input.tool === 'tab') {
      this.drag = { ...base, mode: 'tabnew' };
      return;
    }
    if (input.tool === 'rect') {
      this.drag = { ...base, mode: 'rectnew' };
      return;
    }
    if (input.tool === 'text') {
      this.onNewText?.(this.snapPoint(world));
      return;
    }

    const handle = this.hitHandle(world);
    if (handle !== null) {
      const rail = (input.spec.rails ?? [])[handle.index]!;
      if (handle.mode === 'railcorner') {
        const [ax, ay] = rail.at as Vec2;
        const [w, hgt] = rail.size as Vec2;
        const corners = rectCorners([ax, ay], [w, hgt]);
        this.drag = {
          ...base,
          mode: handle.mode,
          index: handle.index,
          corner: handle.corner,
          // The drag moves the corner that was grabbed, so that is what the
          // offset applies to — not the rail's origin, which is a different
          // point for three of the four corners.
          origin: [...corners[handle.corner]!],
          rect0: [ax, ay, ax + w, ay + hgt],
        };
      } else {
        this.drag = {
          ...base,
          mode: handle.mode,
          index: handle.index,
          corner: handle.corner,
          origin: [...(rail.points![handle.corner] as Vec2)],
        };
      }
      return;
    }

    const hit = this.hitTest(world);
    this.onSelect?.(hit);

    if (hit === null) {
      this.drag = { ...base, mode: 'pan' };
      this.canvas.classList.add('dragging');
      return;
    }

    const mode: DragMode =
      hit.kind === 'board' ? 'board'
      : hit.kind === 'tab' ? 'tabmove'
      : hit.kind === 'text' ? 'textmove'
      : hit.kind === 'rail' ? 'railmove'
      : 'none';

    if (mode === 'none') {
      this.drag = { ...base, mode: 'pan' };
      return;
    }
    this.drag = { ...base, mode, index: hit.index, origin: this.anchorOf(hit) ?? [0, 0] };
  };

  /** The editable position of a selected object, which a move drag offsets. */
  private anchorOf(selection: Selection): Vec2 | null {
    const spec = this.input?.spec;
    if (spec === undefined) return null;
    switch (selection.kind) {
      case 'board': {
        const p = spec.boards?.[selection.index]?.position;
        return Array.isArray(p) ? [p[0], p[1]] : null;
      }
      case 'tab': {
        const p = spec.tabs?.[selection.index]?.origin;
        return Array.isArray(p) ? [p[0], p[1]] : null;
      }
      case 'text': {
        const p = spec.text?.[selection.index]?.at;
        return Array.isArray(p) ? [p[0], p[1]] : null;
      }
      case 'rail': {
        const rail = spec.rails?.[selection.index];
        return rail !== undefined && Array.isArray(rail.at) ? [rail.at[0], rail.at[1]] : null;
      }
      default:
        return null;
    }
  }

  private onPointerMove = (ev: PointerEvent): void => {
    if (ev.pointerType === 'touch' && this.touches.has(ev.pointerId)) {
      this.touches.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (this.pinch !== null) {
        this.movePinch();
        return;
      }
      if (this.gestureSpent) return;
    }
    this.pointer = this.worldOf(ev);
    this.onPointerAt?.(this.pointer);
    // Only the overlay: the board itself has not changed.
    if (this.input?.crosshair === true) this.requestOverlayDraw();

    const drag = this.drag;
    if (drag === null) {
      this.updateCursor(ev);
      return;
    }
    drag.world = this.worldOf(ev);
    drag.shift = ev.shiftKey;
    if (Math.hypot(ev.clientX - drag.startScreen[0], ev.clientY - drag.startScreen[1]) > 2) {
      drag.moved = true;
    }

    if (drag.mode === 'pan') {
      this.camera.panByScreen(
        ev.clientX - drag.startScreen[0],
        ev.clientY - drag.startScreen[1],
      );
      drag.startScreen = [ev.clientX, ev.clientY];
      this.requestDraw();
      return;
    }

    if (drag.mode === 'tabnew' || drag.mode === 'rectnew') {
      this.requestDraw();
      return;
    }

    // The undo point belongs here, before the first live edit — taking it when
    // the drag ends would snapshot the moved state and make undo a no-op.
    if (!drag.begun && EDITING_MODES.has(drag.mode)) {
      drag.begun = true;
      this.onEditBegin?.();
    }

    // Live edits go through the same commit path as everything else, so the
    // preview and the canvas can never disagree about where a thing is.
    const change = this.moveChange(drag);
    if (change !== null) this.onPreviewChange?.(change);
  };

  private moveChange(drag: DragState): ((spec: PanelSpec) => void) | null {
    const dx = drag.world[0] - drag.startWorld[0];
    const dy = drag.world[1] - drag.startWorld[1];
    const moved: Vec2 = [this.snap(drag.origin[0] + dx), this.snap(drag.origin[1] + dy)];
    const index = drag.index;

    switch (drag.mode) {
      case 'board':
        return (spec) => {
          const board = spec.boards?.[index];
          if (board) board.position = moved;
        };
      case 'tabmove':
        return (spec) => {
          const tab = spec.tabs?.[index];
          if (tab) tab.origin = moved;
        };
      case 'textmove':
        return (spec) => {
          const item = spec.text?.[index];
          if (item) item.at = moved;
        };
      case 'railmove':
        return (spec) => {
          const rail = spec.rails?.[index];
          if (rail && Array.isArray(rail.at)) rail.at = moved;
        };
      case 'polyvertex':
        return (spec) => {
          const rail = spec.rails?.[index];
          if (rail?.points?.[drag.corner]) rail.points[drag.corner] = moved;
        };
      case 'railcorner': {
        // Corners: 0 = top-left, 1 = top-right, 2 = bottom-left, 3 = bottom-right.
        let [x0, y0, x1, y1] = drag.rect0;
        if (drag.corner === 0) { x0 = moved[0]; y0 = moved[1]; }
        else if (drag.corner === 1) { x1 = moved[0]; y0 = moved[1]; }
        else if (drag.corner === 2) { x0 = moved[0]; y1 = moved[1]; }
        else { x1 = moved[0]; y1 = moved[1]; }

        const nw = x1 - x0;
        const nh = y1 - y0;
        // A rail dragged inside-out would silently become a zero or negative
        // rectangle, so refuse rather than write nonsense into the spec.
        if (nw < 0.1 || nh < 0.1) return null;
        return (spec) => {
          const rail = spec.rails?.[index];
          if (rail) {
            rail.at = [x0, y0];
            rail.size = [nw, nh];
          }
        };
      }
      default:
        return null;
    }
  }

  /**
   * Show a resize cursor over a rail handle.
   *
   * Handles are small, and without this the only way to discover one is to try
   * dragging and see what happens.
   */
  private updateCursor(ev: PointerEvent): void {
    const input = this.input;
    if (input === null) return;
    if (input.tool !== 'select') {
      this.canvas.style.cursor = '';
      return;
    }

    const handle = this.hitHandle(this.worldOf(ev));
    if (handle === null) {
      this.canvas.style.cursor = '';
      return;
    }
    // Corners are ordered top-left, top-right, bottom-left, bottom-right.
    this.canvas.style.cursor =
      handle.mode === 'polyvertex' ? 'move'
      : handle.corner === 0 || handle.corner === 3 ? 'nwse-resize'
      : 'nesw-resize';
  }

  /** The direction a new-tab drag is aiming, snapped to 45° unless Shift is held. */
  private dragDirection(drag: DragState): Vec2 {
    const dx = drag.world[0] - drag.startWorld[0];
    const dy = drag.world[1] - drag.startWorld[1];
    // A click without a drag means "straight right", the commonest case.
    if (Math.hypot(dx, dy) < 0.5) return [1, 0];
    if (drag.shift) {
      const len = Math.hypot(dx, dy);
      return [Math.round((dx / len) * 100) / 100, Math.round((dy / len) * 100) / 100];
    }
    const step = Math.PI / 4;
    const angle = Math.round(Math.atan2(dy, dx) / step) * step;
    return [Math.round(Math.cos(angle) * 100) / 100, Math.round(Math.sin(angle) * 100) / 100];
  }

  private onPointerUp = (ev: PointerEvent): void => {
    if (ev.pointerType === 'touch') {
      this.touches.delete(ev.pointerId);
      if (this.pinch !== null || this.gestureSpent) {
        this.pinch = null;
        this.gestureSpent = this.touches.size > 0;
        if (this.canvas.hasPointerCapture(ev.pointerId)) this.canvas.releasePointerCapture(ev.pointerId);
        return;
      }
    }
    const drag = this.drag;
    this.drag = null;
    this.canvas.classList.remove('dragging');
    if (drag === null) return;
    if (this.canvas.hasPointerCapture(ev.pointerId)) {
      this.canvas.releasePointerCapture(ev.pointerId);
    }

    if (drag.mode === 'tabnew') {
      this.onNewTab?.(this.snapPoint(drag.startWorld), this.dragDirection(drag));
      this.requestDraw();
      return;
    }

    if (drag.mode === 'rectnew') {
      const x = Math.min(drag.startWorld[0], drag.world[0]);
      const y = Math.min(drag.startWorld[1], drag.world[1]);
      const w = Math.abs(drag.world[0] - drag.startWorld[0]);
      const h = Math.abs(drag.world[1] - drag.startWorld[1]);
      if (w >= 0.2 && h >= 0.2) {
        this.onNewRect?.(this.snapPoint([x, y]), [this.snap(w), this.snap(h)]);
      }
      this.requestDraw();
      return;
    }

    // A move that actually moved becomes one undo step; a plain click does not.
    if (drag.moved) {
      const change = this.moveChange(drag);
      if (change !== null) this.onCommit?.(change);
    }
    this.requestDraw();
  };

  /**
   * A second finger turns whatever the first one started into a pinch. A move
   * already under way has edited the spec live, so it is committed as it
   * stands; a rubber band or a pan is simply dropped.
   */
  private startPinch(ev: PointerEvent): void {
    const drag = this.drag;
    this.drag = null;
    this.canvas.classList.remove('dragging');
    if (drag !== null && drag.begun && drag.moved) {
      const change = this.moveChange(drag);
      if (change !== null) this.onCommit?.(change);
    }
    this.canvas.setPointerCapture(ev.pointerId);
    this.pinch = this.pinchState();
    this.requestDraw();
  }

  /** Zoom by the change in finger spread, and pan with the midpoint, as map apps do. */
  private movePinch(): void {
    const from = this.pinch;
    const to = this.pinchState();
    if (from === null || to === null) return;
    if (from.dist > 0 && to.dist > 0) this.camera.zoomAt(from.x, from.y, to.dist / from.dist);
    this.camera.panByScreen(to.x - from.x, to.y - from.y);
    this.pinch = to;
    this.requestDraw();
  }

  private pinchState(): { dist: number; x: number; y: number } | null {
    const [p, q] = [...this.touches.values()];
    if (p === undefined || q === undefined) return null;
    const rect = this.canvas.getBoundingClientRect();
    return {
      dist: Math.hypot(q.x - p.x, q.y - p.y),
      x: (p.x + q.x) / 2 - rect.left,
      y: (p.y + q.y) / 2 - rect.top,
    };
  }

  /** Drop a source board onto the canvas at a screen position. */
  worldAtClient(clientX: number, clientY: number): Vec2 {
    return this.snapPoint(this.worldOf({ clientX, clientY }));
  }

  /** Zoom about the centre of the canvas, for buttons that have no cursor position. */
  zoomBy(factor: number): void {
    const { width, height } = this.viewport;
    this.camera.zoomAt(width / 2, height / 2, factor);
    this.requestDraw();
  }

  /** The middle of the current view, where a double-clicked source lands. */
  viewCentre(): Vec2 {
    const view = this.viewport;
    return this.snapPoint([
      this.camera.toWorldX(view.width / 2),
      this.camera.toWorldY(view.height / 2),
    ]);
  }
}

function ringPath(path: Path2D, ring: ReadonlyArray<Point>): void {
  if (ring.length === 0) return;
  path.moveTo(ring[0]![0], ring[0]![1]);
  for (let i = 1; i < ring.length; i++) path.lineTo(ring[i]![0], ring[i]![1]);
  path.closePath();
}

function rectCorners(at: Vec2, size: Vec2): Vec2[] {
  return [
    [at[0], at[1]],
    [at[0] + size[0], at[1]],
    [at[0], at[1] + size[1]],
    [at[0] + size[0], at[1] + size[1]],
  ];
}


